import React, { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_PROJECT, PROJECTS } from "../graphql/gqlProject";
import { Poleset, ProjectStatus } from "../graphql/gqlTypes";
import { useParams } from "react-router-dom";
import { SnackbarContext } from "../contexts/snackbarContext";
import { T } from "../components/T";
import { POLESETS } from "../graphql/gqlPoleset";

const CreateNewProjectModal = ({
  isOpen,
  close,
}: {
  isOpen: boolean;
  close: () => void;
}) => {
  const { userId } = useParams();
  const theme = useTheme();

  const [poleset, setPoleset] = useState("__default__");
  const polesetsQuery = useQuery(POLESETS, {
    variables: { userId },
  });
  const polesets: Poleset[] = polesetsQuery?.data?.getPolesetsByUserId || [];

  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const { openSuccessSnackbar, openErrorSnackbar } =
    useContext(SnackbarContext);

  const [createProject] = useMutation(CREATE_PROJECT, {
    onCompleted: () => {
      close();
      setNewProjectName("");
      setNewProjectDescription("");
      openSuccessSnackbar(<T str="Project successfully created" />);
    },
    onError: (error) => {
      openErrorSnackbar(error.message || <T str="Failed to create project" />);
    },
    refetchQueries: [
      {
        query: PROJECTS,
        variables: { userId },
      },
    ],
  });

  const handleCreateProject = () => {
    createProject({
      variables: {
        name: newProjectName,
        description: newProjectDescription,
        status: ProjectStatus.PRIVATE,
        polesetId: poleset === "__default__" ? null : poleset,
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <DialogTitle>
        <T str="Create new project" />
      </DialogTitle>
      <DialogContent>
        <TextField
          label={<T str="Project name" />}
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          fullWidth
          color="secondary"
          required
          sx={{
            mb: 2,
            mt: 2,
          }}
        />
        <TextField
          label={<T str="Description" />}
          value={newProjectDescription}
          onChange={(e) => setNewProjectDescription(e.target.value)}
          fullWidth
          multiline
          rows={3}
          color="secondary"
          sx={{
            mb: 2,
          }}
        />
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel color="secondary">
            <T str="Poleset" />
          </InputLabel>
          <Select
            value={poleset}
            label="Poleset"
            onChange={(e) => setPoleset(e.target.value)}
            color="secondary"
          >
            <MenuItem value={"__default__"}>
              <T str="Default poleset" />
            </MenuItem>
            {polesets.map((set) => {
              return (
                <MenuItem key={set.id} value={set.id}>
                  {set.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={close}>
          <T str="Cancel" />
        </Button>
        <Button
          onClick={handleCreateProject}
          variant="contained"
          color="secondary"
          disabled={!newProjectName}
        >
          <T str="Create" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNewProjectModal;
